import { Inject, Injectable, PLATFORM_ID, makeStateKey } from '@angular/core'
import { isPlatformBrowser } from '@angular/common'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { TransferState } from '@angular/core'
import { firstValueFrom } from 'rxjs'

import { environment } from '@app-environments/environment'
import { CookieAuthStorageService } from '@app-services/auth-storage/cookie-auth-storage.service'
import {
    DEALER_SEARCH_QUERY,
    DealerSearchInput,
    DealerSearchResult,
} from '@app-domains/meilisearch/types/dealer-search.types'

type GraphQlDealerSearchResponse = {
    data?: { dealerSearch: DealerSearchResult }
    errors?: Array<{ message: string }>
}

@Injectable({ providedIn: 'root' })
export class DealerSearchGatewayService {
    constructor(
        private readonly httpClient: HttpClient,
        private readonly transferState: TransferState,
        private readonly authStateStore: CookieAuthStorageService,
        @Inject(PLATFORM_ID) private readonly platformId: object,
    ) {}

    public async search(input: DealerSearchInput): Promise<DealerSearchResult> {
        const isBrowser = isPlatformBrowser(this.platformId)
        const cacheKey = makeStateKey<string>(`dealerSearch(${JSON.stringify(input)})`)

        if (! isBrowser && this.transferState.hasKey(cacheKey)) {
            return JSON.parse(this.transferState.get(cacheKey, '{}')) as DealerSearchResult
        }

        const url = `${environment.api.schema}://${environment.api.hostname}/graphql?op=DealerSearch`
        const token = this.authStateStore.getAuthState()?.accessToken
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        })

        const response = await firstValueFrom(this.httpClient.post<GraphQlDealerSearchResponse>(url, {
            operationName: 'DealerSearch',
            query: DEALER_SEARCH_QUERY,
            variables: { input },
        }, { headers }))

        if (response.errors?.length) {
            throw new Error(response.errors.map((error) => error.message).join('; '))
        }

        const result = response.data?.dealerSearch
        if (! result) {
            throw new Error('dealerSearch returned no data')
        }

        if (! isBrowser) {
            this.transferState.set(cacheKey, JSON.stringify(result))
        }

        return result
    }
}
